import { Descriptions } from 'antd';
import {
  ActionButton,
  DataObjectGroupType,
  DetailLayout,
  ErrorAlertWithDrawer,
  GetApiErrorMessage,
  isLocked,
  LockedBy,
  ObjectHeader,
  ObjectType,
  Padding,
  PermissionCode,
  PopconfirmDelete,
  Spinner,
  useCheckLockAndNavigate,
  useDataObjectGroupQuery,
  useHasPermission,
  useMutateDataObjectGroup,
  useMutationErrors,
  useShowLockData,
  useAlertContext,
  InfoIconUrl,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataObjectGroupDetails.translations.json';
import { useParams } from 'react-router-dom';
import { path } from '../../paths';
import { UiLink } from '../../paths/UiLink';
import { DataObjectGroupBreadcrumbs } from './DataObjectGroupBreadcrumbs';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useCallback } from 'react';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'data-object-group-details',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const DataObjectGroupDetails = () => {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const dataObjectGroupId = Number(params.id);
  if (isNaN(dataObjectGroupId))
    throw new Error('Data object group ID not provided');

  const { data: dataObjectGroup } = useDataObjectGroupQuery({
    id: dataObjectGroupId,
  });

  const { lockData, refetchLockData } = useShowLockData(
    ObjectType.DataObjectGroup,
    dataObjectGroupId,
  );
  const navigateToIndex = useNavigateTo(path.dataObjectGroupDirectory);
  const navigateToDataObjectGroupEdit = useNavigateTo(
    path.dataObjectGroupEdit,
    {
      id: dataObjectGroupId,
    },
  );
  const checkLockAndNavigateToEdit = useCheckLockAndNavigate(
    refetchLockData,
    lockData,
    navigateToDataObjectGroupEdit,
  );

  const { mutate } = useMutateDataObjectGroup();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { setSuccess } = useAlertContext();
  const handleDelete = useCallback(() => {
    clearMutationErrors();
    mutate(
      { id: dataObjectGroupId },
      {
        onSuccess: () => {
          navigateToIndex();
          setSuccess(t('alert.success.delete'));
        },
        onError: ({ response }) => {
          setMutationErrors(response?.data.errors || []);
        },
      },
    );
  }, [
    clearMutationErrors,
    mutate,
    dataObjectGroupId,
    navigateToIndex,
    setSuccess,
    t,
    setMutationErrors,
  ]);

  const hasPermissionToManage = useHasPermission(
    PermissionCode.DATA_OBJECT_GROUP_MANAGE,
  );

  if (!dataObjectGroup) {
    return <Spinner />;
  }

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.DATA_OBJECT_GROUP_DETAILS}
      type="detail"
      breadCrumb={<DataObjectGroupBreadcrumbs data={dataObjectGroup} />}
      headerConfig={{
        actions: (
          <>
            {dataObjectGroup.groupType === DataObjectGroupType.I ? (
              <UiLink
                id="viewRelatedInputs-link"
                path={path.inputDataObjectDirectory}
                params={{ groupIds: dataObjectGroup.id, statusCodes: '' }}
                target="_blank"
              >
                {({ type, ...rest }) => (
                  <ActionButton
                    label={t('button.view-related-inputs')}
                    layoutType="detail"
                    type="abort"
                    {...rest}
                  />
                )}
              </UiLink>
            ) : (
              <UiLink
                id="viewRelatedOutputs-link"
                path={path.outputDataObjectDirectory}
                params={{ groupIds: dataObjectGroup.id, statusCodes: '' }}
                target="_blank"
              >
                {({ type, ...rest }) => (
                  <ActionButton
                    label={t('button.view-related-outputs')}
                    layoutType="detail"
                    type="abort"
                    {...rest}
                  />
                )}
              </UiLink>
            )}
            {hasPermissionToManage && (
              <ActionButton
                disabled={isLocked(lockData)}
                type="mainAction"
                layoutType="detail"
                onClick={() => checkLockAndNavigateToEdit()}
              />
            )}
          </>
        ),
        dropdownMenu: hasPermissionToManage
          ? {
              items: [
                {
                  disabled: isLocked(lockData),
                  key: 'delete',
                  label: (
                    <PopconfirmDelete
                      stopClickPropagation
                      onDelete={handleDelete}
                      genitiveModelName={t('delete.genitive')}
                    />
                  ),
                },
              ],
            }
          : undefined,
      }}
      side={{
        header: (
          <Padding>
            <ObjectHeader
              upperText={dataObjectGroup.code}
              lowerText={dataObjectGroup.name}
            />
          </Padding>
        ),
        content: (
          <Padding>
            <Descriptions
              column={1}
              items={[
                {
                  key: 'groupType',
                  label: t('groupType.label'),
                  children: t('groupType.' + dataObjectGroup.groupType),
                },
                {
                  key: 'description',
                  label: t('description.label'),
                  children: dataObjectGroup.description,
                },
              ]}
            />
          </Padding>
        ),
        footer: (
          <LockedBy
            id={dataObjectGroup.id}
            entity={dataObjectGroup}
            objectType={ObjectType.DataObjectGroup}
          />
        ),
      }}
      alert={<ErrorAlertWithDrawer validationMessagesMap={mutationErrors} />}
    />
  );
};
